// services/message.service.js
import ApiService from './api.service';
import { formatDate } from '../utils/dateUtils';

const MessageService = {
    // API calls
    getConversations: () => ApiService.fetch('messages/conversations'),

    getMessages: (userId) => {
        console.log('Getting messages for user ID:', userId);
        return ApiService.fetch(`messages/${userId}`);
    },

    sendMessage: (receiverId, content) => {
        console.log('Sending message to:', receiverId, 'Content:', content);
        return ApiService.fetch('messages/send', {
            method: 'POST',
            body: JSON.stringify({ receiver_id: receiverId, content })
        });
    },

    markAsRead: (userId) => ApiService.fetch(`messages/${userId}/read`, {
        method: 'PUT'
    }),

    deleteUserMessages: (userId) => ApiService.fetch(`messages/${userId}`, {
        method: 'DELETE'
    }),

    // Business logic functions
    processContactsFromAppointments: (appointments, userRole, specialtyField) => {
        const contactsMap = new Map();

        appointments.forEach(appointment => {
            // Usa gli ID corretti per identificare i contatti
            const contactId = userRole === 'patient' ?
                (appointment.therapist_user_id || appointment.therapist_id) :
                (appointment.patient_user_id || appointment.patient_id);

            const contactRoleId = userRole === 'patient' ?
                appointment.therapist_id :
                appointment.patient_id;

            const contactName = userRole === 'patient' ?
                appointment.therapist_name :
                appointment.patient_name;

            const profilePicture = userRole === 'patient' ?
                appointment.therapist_profile_picture :
                appointment.patient_profile_picture;

            // Aggiorna il contatto se esiste già, altrimenti lo aggiunge
            if (!contactsMap.has(contactId)) {
                contactsMap.set(contactId, {
                    id: contactId,          // ID utente per le operazioni di messaggistica
                    role_id: contactRoleId, // ID del ruolo (doctor_id o patient_id) per riferimenti
                    first_name: contactName.split(' ')[0],
                    last_name: contactName.split(' ').slice(1).join(' '),
                    profile_picture: profilePicture,
                    appointments: [appointment],
                    // Per i pazienti, usa l'ultima speciality; per i dottori, usa la data dell'ultimo appuntamento
                    [specialtyField]: userRole === 'patient'
                        ? appointment.specialization
                        : `Prossimo Appuntamento: ${formatDate(appointment.date)}`

                });
            } else {
                const existingContact = contactsMap.get(contactId);
                existingContact.appointments.push(appointment);

                // Aggiorna il campo di specialità/data in base agli appuntamenti più recenti
                if (userRole === 'doctor') {
                    const latestAppointment = [...existingContact.appointments].sort(
                        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                    )[0];
                    existingContact[specialtyField] = `Prossimo Appuntamento: ${formatDate(latestAppointment.date)}`;
                }
            }
        });

        // Converti la mappa in array e ordina per data di appuntamento (più recenti prima)
        const contactsArray = Array.from(contactsMap.values())
            .sort((a, b) => {
                const latestAppA = a.appointments.sort((x, y) => new Date(y.date).getTime() - new Date(x.date).getTime())[0];
                const latestAppB = b.appointments.sort((x, y) => new Date(y.date).getTime() - new Date(x.date).getTime())[0];
                return new Date(latestAppA.date).getTime() - new Date(latestAppB.date).getTime();
            });

        return contactsArray;
    },

    filterContactsBySearchTerm: (contacts, searchTerm) => {
        return contacts.filter(contact => {
            const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
            return fullName.includes(searchTerm.toLowerCase());
        });
    },

    filterContactsWithoutConversations: (contacts, conversations) => {
        return contacts.filter(contact =>
            !conversations.some(conv => conv.id === contact.id)
        );
    },

    findContactById: (contacts, id) => {
        return contacts.find(c => c.id === parseInt(id));
    },

    getIdForApiCall: (contact, defaultId) => {
        return contact && contact.role_id ? contact.role_id : defaultId;
    },

    // Message handling functions - These will be called from the component
    // 🔧 SOLUZIONE SEMPLICE: Non usare getIdForApiCall per i messaggi
    loadMessagesForContact: async (userId, appointmentContacts, conversations, setLoading, setCurrentMessages, setSelectedConversation) => {
        try {
            setLoading(true);

            // 🎯 FIX: Usa direttamente userId per i messaggi (è sempre l'ID utente corretto)
            const data = await MessageService.getMessages(userId);
            setCurrentMessages(data);

            // Cerca il contatto nelle conversazioni
            let selected = MessageService.findContactById(conversations, userId);

            // Se non trovato nelle conversazioni, cerca nei contatti da appuntamento
            if (!selected) {
                const contact = MessageService.findContactById(appointmentContacts, userId);
                selected = contact;
            }

            setSelectedConversation(selected);

            // Segna i messaggi come letti
            await MessageService.markAsRead(userId);
        } catch (error) {
            console.error('[DEBUG] Error loading messages:', error);
        } finally {
            setLoading(false);
        }
    },

    sendMessageToContact: async (messageContent, conversationId, appointmentContacts, conversations, setMessageContent, loadMessages, loadConversations) => {
    if (!messageContent.trim() || !conversationId) return;

    try {
        // 🎯 FIX: Usa direttamente conversationId per inviare i messaggi
        await MessageService.sendMessage(conversationId, messageContent);
        setMessageContent('');
        loadMessages(conversationId);

        // Se questa è la prima volta che scriviamo a questo contatto,
        // ricarica le conversazioni per aggiornare la lista
        const existingConversation = MessageService.findContactById(conversations, conversationId);
        if (!existingConversation) {
            loadConversations();
        }
    } catch (error) {
        console.error('[DEBUG] Error sending message:', error);
    }
},

    loadAppointmentContactsData: async (userRole, specialtyField, setLoading, setAppointmentContacts, AppointmentService) => {
        try {
            let appointments;

            if (userRole === 'patient') {
                appointments = await AppointmentService.getPatientAppointments();
            } else {
                appointments = await AppointmentService.getDoctorAppointments();
            }

            const contactsArray = MessageService.processContactsFromAppointments(
                appointments,
                userRole,
                specialtyField
            );

            setAppointmentContacts(contactsArray);
        } catch (error) {
            console.error('[DEBUG] Error loading appointment contacts:', error);
        } finally {
            if (setLoading) setLoading(false);
        }
    },

    loadConversationsData: async (setLoading, setConversations) => {
        try {
            setLoading(true);
            const data = await MessageService.getConversations();
            setConversations(data);
        } catch (error) {
            console.error('[DEBUG] Error loading conversations:', error);
        } finally {
            if (setLoading) setLoading(false);
        }
    }
};

export default MessageService;